"use client";

import * as React from "react";
import { cn } from "@baluu/ui/lib/utils";
import { Minus, Plus, Lock, Info, AlertCircle, Tag } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { TicketQuantityInput } from "./ticket-quantity-input";
import { TicketPolicyDialog } from "./ticket-selector";
import type { TicketBookingPolicy } from "./utils/types";
import type { ValidationError } from "../../lib/pricing";
import {
  TicketConditionsChips,
  type CheckoutTicketSummary,
} from "./ticket-conditions-chips";
import { TicketConditionsPopover } from "./ticket-conditions-popover";
import { ruleDiscountHintLabel, ruleEvaluatedConditions } from "./utils/rule-conditions";

type TicketSelectorV2Props = {
  id: string;
  name: string;
  description?: string;
  price?: number;
  currency?: string;
  quantity: number;
  minQuantity?: number;
  maxQuantity?: number;
  disabled?: boolean;
  soldOut?: boolean;
  onChange: (quantity: number) => void;
  className?: string;
  ticketTypeGroup?: "regular" | "deposit";
  depositAmount?: number;
  fullPrice?: number;
  priceSuffix?: string;
  membershipRequired?: boolean;
  membershipAccessGranted?: boolean;
  membershipsUrl?: string;
  eligibilityMessage?: string;
  policyHint?: string;
  bookingPolicy?: TicketBookingPolicy | null;
  onPolicyClick?: (ticketName: string, policy: TicketBookingPolicy) => void;
  validationErrors?: ValidationError[];
  pricingRules?: any[];
  checkoutSummary?: CheckoutTicketSummary | null;
};

function currencySymbol(currency: string = "GBP"): string {
  const symbols: Record<string, string> = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };
  return symbols[currency] || currency;
}

function formatPrice(price: number, currency: string = "GBP"): string {
  return `${currencySymbol(currency)}${(price / 100).toFixed(2)}`;
}

function TicketSelectorV2({
  name,
  description,
  price,
  currency = "GBP",
  quantity,
  minQuantity = 0,
  maxQuantity = 10,
  disabled = false,
  soldOut = false,
  onChange,
  className,
  ticketTypeGroup,
  depositAmount,
  fullPrice,
  priceSuffix,
  membershipRequired = false,
  membershipAccessGranted = false,
  membershipsUrl,
  eligibilityMessage,
  bookingPolicy,
  onPolicyClick,
  validationErrors = [],
  pricingRules = [],
  checkoutSummary = null,
}: TicketSelectorV2Props) {
  const [isPolicyDialogOpen, setIsPolicyDialogOpen] = React.useState(false);
  const [isConditionsOpen, setIsConditionsOpen] = React.useState(false);
  const isMembersOnlyLocked = membershipRequired && !membershipAccessGranted;
  const isIneligible = !!eligibilityMessage;
  // Reducing a quantity you already hold must ALWAYS be possible, sold out or
  // not. A cap can tighten below a selection already made — pick a session or
  // slot with room, choose 2, then switch to one with room for 1 — and the row
  // flips to sold out with the selection still in state. Gating the decrement
  // on !soldOut stranded it: the customer could neither remove it nor book it,
  // and the tickets step's continue check only counts total tickets, so it
  // went to payment and was refused there.
  // Not gated on soldOut, isMembersOnlyLocked or isIneligible. Every one of
  // those can turn true UNDER a quantity already held — a tighter slot for the
  // first, a session-dependent eligibility rule for the other two — and the
  // tickets step's continue check only counts total tickets, so a quantity you
  // cannot remove still goes to payment and is refused there.
  const canDecrease = quantity > 0 && !disabled;
  // Every flag the render condition below relaxes for hand-back must appear
  // HERE, or relaxing it hands back an increment too. maxQuantity reflects
  // availability and pool caps only — membership and eligibility never zero
  // it — so without these an ineligible row holding a quantity would offer a
  // live "+" on a ticket the customer is not allowed to buy.
  const canIncrease =
    quantity < maxQuantity &&
    !disabled &&
    !soldOut &&
    !isMembersOnlyLocked &&
    !isIneligible;
  const [quantityNote, setQuantityNote] = React.useState<string>();
  const [isAnimating, setIsAnimating] = React.useState<
    "increase" | "decrease" | null
  >(null);

  const hasErrors = validationErrors.length > 0;

  const handleDecrease = () => {
    if (canDecrease) {
      setIsAnimating("decrease");
      setQuantityNote(undefined);
      const newQuantity = quantity - 1;
      onChange(newQuantity < minQuantity ? 0 : newQuantity);
      setTimeout(() => setIsAnimating(null), 150);
    }
  };

  const handleIncrease = () => {
    if (canIncrease) {
      setIsAnimating("increase");
      // From 0, jump straight to the per-order minimum: 1..min-1 can never be
      // booked, so a "Min. 8" ticket shouldn't take eight taps. Say why the
      // number jumped so it doesn't read as a glitch.
      const next = Math.min(Math.max(quantity + 1, minQuantity), maxQuantity);
      setQuantityNote(
        next > quantity + 1 ? `Minimum ${minQuantity} per order` : undefined,
      );
      onChange(next);
      setTimeout(() => setIsAnimating(null), 150);
    }
  };

  const priceDisplay = React.useMemo(() => {
    // Suppressed for a sold-out row only while it holds NOTHING. A row can now
    // be sold out with a quantity still on it — a cap tightening under a
    // selection — and that row is rendered and interactive, so hiding its
    // price would show a live quantity with no price beside it while the order
    // summary keeps billing for it.
    if (soldOut && quantity === 0) return null;

    if (
      ticketTypeGroup === "deposit" &&
      depositAmount !== undefined &&
      fullPrice !== undefined
    ) {
      if (depositAmount === 0) return "Free";
      return (
        <>
          Deposit: {formatPrice(depositAmount, currency)} / Full:{" "}
          {formatPrice(fullPrice, currency)}
        </>
      );
    }

    if (price !== undefined) {
      return price === 0 ? "Free" : formatPrice(price, currency);
    }

    return null;
  }, [soldOut, quantity, ticketTypeGroup, depositAmount, fullPrice, price, currency]);

  const minQtyHint =
    minQuantity > 1 ? `Min. ${minQuantity} per order` : undefined;

  return (
    <div
      className={cn(
        "p-4 bg-card rounded-lg border border-border",
        "transition-shadow duration-200",
        quantity > 0 && !hasErrors && "border-primary/30 shadow-sm",
        hasErrors && "border-destructive/40",
        // A sold-out row you are HOLDING something in stays at full opacity: its
        // decrement is the only way to unblock the step, and a dimmed card reads
        // as inert. Dim it once there is nothing left to act on.
        (disabled ||
          ((soldOut || isMembersOnlyLocked || isIneligible) &&
            quantity === 0)) &&
          "opacity-50",
        className,
      )}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        {/* Ticket Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isMembersOnlyLocked && (
              <Lock className="h-4 w-4 text-muted-foreground" />
            )}
            <h4 className="font-medium text-foreground">{name}</h4>
            {minQtyHint && (
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-muted text-muted-foreground">
                {minQtyHint}
              </span>
            )}
            {membershipRequired && (
              <span
                className={cn(
                  "text-xs font-medium px-2 py-0.5 rounded",
                  membershipAccessGranted
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {membershipAccessGranted ? "Member" : "Members Only"}
              </span>
            )}
            {soldOut && (
              <span className="text-xs font-medium text-destructive-foreground bg-destructive/10 px-2 py-0.5 rounded">
                Sold out
              </span>
            )}
          </div>

          {priceDisplay && (
            <p className="text-base font-semibold text-primary mt-1">
              {priceDisplay}
              {priceSuffix && (
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  {priceSuffix}
                </span>
              )}
            </p>
          )}

          {checkoutSummary &&
            (checkoutSummary.inlineChips.length > 0 ||
              checkoutSummary.constraints.length > 0 ||
              checkoutSummary.policy) && (
              <TicketConditionsPopover
                open={isConditionsOpen}
                onOpenChange={setIsConditionsOpen}
                chips={checkoutSummary.inlineChips}
                constraints={checkoutSummary.constraints}
                policy={checkoutSummary.policy}
                trigger={
                  <span className="mt-1 inline-block">
                    <TicketConditionsChips
                      chips={checkoutSummary.inlineChips}
                      hasInfo={
                        checkoutSummary.constraints.length > 0 ||
                        !!checkoutSummary.policy
                      }
                      onInfoClick={() => setIsConditionsOpen(true)}
                    />
                  </span>
                }
              />
            )}

          {description && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {description}
            </p>
          )}

          <TicketDiscountHints
            rules={pricingRules}
            currency={currency}
          />

          {bookingPolicy && !isIneligible && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (onPolicyClick) {
                  onPolicyClick(name, bookingPolicy);
                } else {
                  setIsPolicyDialogOpen(true);
                }
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 font-medium mt-1 text-left transition-colors"
            >
              Cancellation Policy
            </button>
          )}

          {isIneligible && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
              <Info className="h-3 w-3 shrink-0" />
              {eligibilityMessage}
            </p>
          )}
        </div>

        {isMembersOnlyLocked && membershipsUrl && (
          <div className="pt-2 md:pt-0">
            <a
              href={membershipsUrl}
              className="text-sm text-primary hover:underline font-medium"
            >
              Become a Member →
            </a>
          </div>
        )}

        {/* Quantity Controls */}
        {/* Rendered for a sold-out, members-locked or ineligible row too WHEN
            a quantity is held, so it can be given back — see canDecrease.
            canIncrease carries all three flags, so the control that appears is
            hand-back only. */}
        {((!soldOut && !isMembersOnlyLocked && !isIneligible) ||
          quantity > 0) && (
          <div className="flex items-center justify-between md:justify-end gap-3 pt-2 md:pt-0 border-t md:border-t-0 border-border/50">
            <span className="text-sm text-muted-foreground md:hidden">
              Quantity
            </span>

            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                onClick={handleDecrease}
                disabled={!canDecrease}
                className={cn(
                  "rounded-full min-w-[44px] min-h-[44px] touch-manipulation",
                  "transition-all duration-150 ease-out",
                  canDecrease && "hover:scale-110 active:scale-95",
                  isAnimating === "decrease" && "scale-90",
                )}
              >
                <Minus className="w-4 h-4" />
              </Button>

              <TicketQuantityInput
                ticketName={name}
                quantity={quantity}
                minQuantity={minQuantity}
                maxQuantity={maxQuantity}
                canIncrease={canIncrease}
                canDecrease={canDecrease}
                onChange={onChange}
                onNote={setQuantityNote}
              />

              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                onClick={handleIncrease}
                disabled={!canIncrease}
                className={cn(
                  "rounded-full min-w-[44px] min-h-[44px] touch-manipulation",
                  "transition-all duration-150 ease-out",
                  canIncrease && "hover:scale-110 active:scale-95",
                  isAnimating === "increase" && "scale-90",
                )}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {quantityNote && (
        <p
          aria-live="polite"
          className="mt-2 text-xs text-muted-foreground md:text-right"
        >
          {quantityNote}
        </p>
      )}

      {/* Validation errors inside the card */}
      {hasErrors && (
        <div className="mt-3 pt-3 border-t border-destructive/20 space-y-1">
          {validationErrors.map((error, idx) => (
            <p
              key={idx}
              className="text-xs text-destructive flex items-center gap-1.5"
            >
              <AlertCircle className="h-3 w-3 shrink-0" />
              {error.message}
            </p>
          ))}
        </div>
      )}

      {bookingPolicy && !onPolicyClick && (
        <TicketPolicyDialog
          isOpen={isPolicyDialogOpen}
          onClose={() => setIsPolicyDialogOpen(false)}
          ticketName={name}
          policy={bookingPolicy}
        />
      )}
    </div>
  );
}

// --- Per-ticket discount hints ---

type TicketRuleHint = {
  name: string;
  discount: string;
  conditions: string[];
};

function formatRuleCondition(c: any): string | null {
  switch (c.conditionType) {
    case "MIN_QUANTITY": return `${c.config.minQuantity}+ tickets`;
    case "MIN_SESSIONS": return `${c.config.minSessions}+ sessions`;
    case "SESSION_WINDOW": return `within ${c.config.timeWindowDays} days`;
    case "BOOKING_ADVANCE": return `${c.config.daysBeforeEvent}+ days early`;
    case "LAST_MINUTE": return `within ${c.config.hoursBeforeEvent}h of event`;
    case "MEMBER_TAGS": return `members only`;
    case "RETURNING_CUSTOMER": return `returning customers`;
    case "FIRST_TIME_BOOKING": return `first-time customers`;
    case "CONSECUTIVE_SESSIONS": return `${c.config.minConsecutive}+ consecutive weeks`;
    default: return null;
  }
}

function buildTicketRuleHints(rules: any[], currency: string): TicketRuleHint[] {
  const symbol = currencySymbol(currency);
  return rules
    .filter((r: any) => r.ruleType === "CONDITIONAL_DISCOUNT" && r.status === "enabled")
    .map((r: any) => {
      // `null` for anything that doesn't lower the price — this panel is a
      // promo surface, and a dialog-authored surcharge is a CONDITIONAL_DISCOUNT
      // too.
      const discount = ruleDiscountHintLabel(r, symbol);
      if (!discount) return null;
      // The SAME source the engine evaluates (`evaluateConditionGroups`:
      // groups first, legacy `conditions` as the fallback) — a dialog-authored
      // rule has its conditions only in `conditionGroups`, so reading
      // `conditions` alone showed no hint for any of them.
      const conditions = ruleEvaluatedConditions(r)
        .map(formatRuleCondition)
        .filter(Boolean) as string[];
      if (conditions.length === 0) return null;
      return { name: r.name, discount, conditions };
    })
    .filter((h): h is TicketRuleHint => h !== null);
}

const MAX_VISIBLE_CONDITIONS = 2;

function TicketDiscountHints({
  rules,
  currency,
}: {
  rules: any[];
  currency: string;
}) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const hints = React.useMemo(() => buildTicketRuleHints(rules, currency), [rules, currency]);

  if (hints.length === 0) return null;

  const allConditions = hints.flatMap((h) =>
    h.conditions.map((c) => ({ condition: c, discount: h.discount, name: h.name })),
  );
  const visibleConditions = isExpanded ? allConditions : allConditions.slice(0, MAX_VISIBLE_CONDITIONS);
  const hiddenCount = allConditions.length - MAX_VISIBLE_CONDITIONS;

  return (
    <div className="mt-2 flex items-start gap-2">
      <Tag className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-px" />
      <div className="min-w-0">
        <span className="text-xs font-semibold text-emerald-700">
          {hints[0].discount}
        </span>
        <div className="mt-0.5 space-y-px">
          {visibleConditions.map((item, i) => (
            <p key={i} className="text-xs text-muted-foreground leading-relaxed">
              {item.condition}
            </p>
          ))}
        </div>
        {hiddenCount > 0 && !isExpanded && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setIsExpanded(true); }}
            className="mt-0.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
          >
            +{hiddenCount} more
          </button>
        )}
        {isExpanded && hiddenCount > 0 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setIsExpanded(false); }}
            className="mt-0.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

type TicketSelectorV2GroupProps = {
  children: React.ReactNode;
  className?: string;
};

function TicketSelectorV2Group({
  children,
  className,
}: TicketSelectorV2GroupProps) {
  return <div className={cn("space-y-3", className)}>{children}</div>;
}

TicketSelectorV2.Group = TicketSelectorV2Group;

export { TicketSelectorV2 };
export type { TicketSelectorV2Props };
